// ═══════════════════════════════════════════════════════════════════════════
// AUTOMEZZI NELLA PROGRAMMAZIONE SETTIMANALE (ottobre 2026) — logica pura
// ═══════════════════════════════════════════════════════════════════════════
// Il mezzo sta dentro la stessa cella della programmazione (persona × riga × giorno),
// accanto a commessa e ore:  mezzoId · mezzoNome · mezzoTarga · mezzoAuto.
//   • mezzoAuto = true  → messo dall'autocompilazione (squadra sullo stesso cantiere o
//                          "tutta la settimana"); se poi la commessa della cella cambia,
//                          il mezzo si toglie da solo, perché non è più "il mezzo di quel
//                          cantiere".
//   • mezzoAuto = false → scelto a mano: resta anche se la commessa cambia.
// Regole di autocompilazione (decise con Paolo, ott 2026):
//   • stessa commessa, stesso giorno, SOLO celle senza mezzo (mai sovrascrivere);
//   • le attività che non sono cantieri (Ufficio, Ferie, Magazzino…) non si autocompilano;
//   • "tutta la settimana": i giorni seguenti della settimana in cui la persona è sulla
//     stessa commessa, più i colleghi su quella commessa in quei giorni — sempre celle vuote.
// L'elenco dei mezzi è quello del Parco mezzi (collezione mezziAttrezzature), solo veicoli.
// ═══════════════════════════════════════════════════════════════════════════

export const ATTIVITA_NON_CANTIERE = ["Ufficio", "Sopralluogo", "Commerciale", "Magazzino", "Ferie", "Permesso", "Malattia"];
export const TIPI_VEICOLO = ["furgone", "auto"];

export const MEZZO_VUOTO = { mezzoId: "", mezzoNome: "", mezzoTarga: "", mezzoAuto: false };

export const etichettaMezzo = (m) => [m?.nome || m?.mezzoNome, m?.targa || m?.mezzoTarga].filter(Boolean).join(" ");

/** Commessa della cella come chiave di confronto ("" se vuota). */
export const chiaveCommessa = (c) => (c?.commessaId || c?.commessaNome || "");

/** La cella è un cantiere su cui ha senso condividere il mezzo? */
export const eCantiere = (c) => !!chiaveCommessa(c) && !ATTIVITA_NON_CANTIERE.includes(c?.commessaNome || "");

const dataDellaChiave = (k) => k.slice(k.lastIndexOf("_") + 1);

/** Dati del mezzo da scrivere in una cella. */
export function datiMezzo(mezzo, automatico) {
  if (!mezzo) return { ...MEZZO_VUOTO };
  return { mezzoId: mezzo.id, mezzoNome: mezzo.nome || "", mezzoTarga: mezzo.targa || "", mezzoAuto: !!automatico };
}

/**
 * Se un aggiornamento cambia la commessa di una cella che aveva un mezzo messo in automatico,
 * aggiunge la pulizia del mezzo. Altrimenti restituisce l'aggiornamento invariato.
 */
export function conCambioCommessa(esistente, updates) {
  if (!esistente?.mezzoId || !esistente.mezzoAuto) return updates;
  const tocca = ("commessaId" in updates) || ("commessaNome" in updates);
  if (!tocca) return updates;
  const prima = chiaveCommessa(esistente);
  const dopo = chiaveCommessa({ ...esistente, ...updates });
  return prima === dopo ? updates : { ...updates, ...MEZZO_VUOTO };
}

/** Celle (chiavi) da autocompilare nello stesso giorno: stessa commessa, senza mezzo. */
export function autocompilaGiorno(celle, chiaveOrigine, dateStr, commessaKey) {
  if (!commessaKey) return [];
  return Object.keys(celle).filter(k => {
    if (k === chiaveOrigine || dataDellaChiave(k) !== dateStr) return false;
    const c = celle[k];
    return eCantiere(c) && chiaveCommessa(c) === commessaKey && !c.mezzoId;
  });
}

/**
 * "Tutta la settimana": dai giorni successivi a quello di partenza (dentro la settimana
 * mostrata), le celle della stessa persona/riga sulla stessa commessa e quelle dei colleghi
 * su quella commessa — solo se senza mezzo.
 */
export function autocompilaSettimana(celle, { uid, rowIndex, dateStr, giorniSettimana, commessaKey }) {
  if (!commessaKey) return [];
  const fuori = new Set();
  for (const d of giorniSettimana) {
    if (d <= dateStr) continue;
    const propria = `${uid}_${rowIndex}_${d}`;
    const c = celle[propria];
    if (!c || !eCantiere(c) || chiaveCommessa(c) !== commessaKey) continue; // la persona quel giorno è altrove
    if (!c.mezzoId) fuori.add(propria);
    autocompilaGiorno(celle, propria, d, commessaKey).forEach(k => fuori.add(k));
  }
  return [...fuori];
}

/** Mezzi assegnati a più cantieri diversi nello stesso giorno: { dateStr: Set(mezzoId) } */
export function conflittiMezzi(celle) {
  const perGiorno = {};
  for (const [k, c] of Object.entries(celle || {})) {
    if (!c?.mezzoId || !chiaveCommessa(c)) continue;
    const d = dataDellaChiave(k);
    perGiorno[d] = perGiorno[d] || {};
    (perGiorno[d][c.mezzoId] = perGiorno[d][c.mezzoId] || new Set()).add(chiaveCommessa(c));
  }
  const out = {};
  for (const [d, m] of Object.entries(perGiorno)) {
    const s = new Set(Object.entries(m).filter(([, comm]) => comm.size > 1).map(([id]) => id));
    if (s.size) out[d] = s;
  }
  return out;
}

/**
 * Ricerca "chi aveva il mezzo" nelle celle di una o più settimane.
 * @param celle   oggetto { "uid_riga_YYYY-MM-DD": cella } (più settimane unite)
 * @returns righe { data, uid, rowIndex, mezzoId, mezzoNome, mezzoTarga, commessaNome, automatico }
 */
export function cercaAssegnazioni(celle, { mezzoId = "", dal, al, uid = "" }) {
  const out = [];
  for (const [k, c] of Object.entries(celle || {})) {
    if (!c?.mezzoId) continue;
    const d = dataDellaChiave(k);
    if (d < dal || d > al) continue;
    if (mezzoId && c.mezzoId !== mezzoId) continue;
    const kuid = k.slice(0, k.indexOf("_"));
    if (uid && kuid !== uid) continue;
    out.push({ data: d, uid: kuid, rowIndex: c.rowIndex ?? 0, mezzoId: c.mezzoId, mezzoNome: c.mezzoNome || "",
      mezzoTarga: c.mezzoTarga || "", commessaNome: c.commessaNome || "", automatico: !!c.mezzoAuto });
  }
  return out.sort((a, b) => a.data.localeCompare(b.data) || a.mezzoNome.localeCompare(b.mezzoNome) || a.uid.localeCompare(b.uid));
}
